import type { PdDataPoint } from "@/hooks/use-etf-live-nav";
import { useCallback, useRef, useState } from "react";

/** API-ready shape for quarterly premium/discount table rows. */
export interface PremiumDiscountQuarterlyRow {
  quarter: string;
  daysAtNav: string;
  daysAtPremium: string;
  daysAtDiscount: string;
  pdRange: string;
}

interface LeveragedPremiumDiscountProps {
  fundType: "leveraged" | "thematic" | "fixed";
  hasPerformanceHistory?: boolean;
  chartPoints?: string;
  dateLabels?: string[];
  quarterlyData?: PremiumDiscountQuarterlyRow[];
  dataPoints?: PdDataPoint[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DEFAULT_DATE_LABELS = ["Dec 30", "Jan 21", "Feb 10", "Mar 4", "Mar 24"];

const DEFAULT_QUARTERLY_DATA: PremiumDiscountQuarterlyRow[] = [
  {
    quarter: "Current Quarter",
    daysAtNav: "\u2014",
    daysAtPremium: "\u2014",
    daysAtDiscount: "\u2014",
    pdRange: "\u2014",
  },
];

function buildFillPath(points: string): string {
  const pairs = points.trim().split(/\s+/);
  if (pairs.length === 0) return "";
  const firstX = pairs[0]?.split(",")[0];
  const lastX = pairs[pairs.length - 1]?.split(",")[0];
  return `M${firstX},80 L${pairs.join(" L")} L${lastX},80 Z`;
}

function lastPoint(points: string): { cx: string; cy: string } {
  const pairs = points.trim().split(/\s+/);
  const last = pairs[pairs.length - 1];
  if (!last) return { cx: "0", cy: "80" };
  const [cx, cy] = last.split(",");
  return { cx: cx ?? "0", cy: cy ?? "80" };
}

const DATE_LABEL_X_POSITIONS = [20, 207, 393, 593, 755];

function fmtDate(dateStr: string): string {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function parsePoints(points: string): { x: number; y: number }[] {
  return points
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(",");
      return { x: Number.parseFloat(x!), y: Number.parseFloat(y!) };
    });
}

export function LeveragedPremiumDiscount({
  fundType,
  hasPerformanceHistory = false,
  chartPoints,
  dateLabels,
  quarterlyData,
  dataPoints,
}: LeveragedPremiumDiscountProps) {
  const hasRealData = chartPoints !== undefined && chartPoints.length > 0;
  const points = chartPoints ?? "";
  const axisMax = fundType === "fixed" ? "0.40" : "0.80";
  const labels = dateLabels ?? DEFAULT_DATE_LABELS;
  const quarters = quarterlyData ?? DEFAULT_QUARTERLY_DATA;

  const pdToleranceLabel = fundType === "fixed" ? "P/D +/- 1%" : "P/D +/- 2%";
  const disclaimerEnding = fundType === "fixed" ? " market price." : ".";
  const fillPath = hasRealData ? buildFillPath(points) : "";
  const endDot = hasRealData ? lastPoint(points) : { cx: "0", cy: "0" };

  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const parsedPoints = hasRealData ? parsePoints(points) : [];

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!dataPoints || dataPoints.length === 0 || !svgRef.current) return;
      const svg = svgRef.current;
      const rect = svg.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * 800;
      let closest = 0;
      let closestDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < parsedPoints.length; i++) {
        const dist = Math.abs(parsedPoints[i]!.x - mouseX);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }
      setHoverIdx(closest);
    },
    [dataPoints, parsedPoints],
  );

  const handleMouseLeave = useCallback(() => setHoverIdx(null), []);

  const hoverPoint = hoverIdx !== null ? parsedPoints[hoverIdx] : null;
  const hoverData = hoverIdx !== null && dataPoints ? dataPoints[hoverIdx] : null;

  return (
    <div
      className="border-b border-[var(--etf-border)]"
      id="sec-premium"
      data-fund-section="twelvedataEnabled twelvedataSymbol"
    >
      <div className="flex items-center justify-between px-10 py-6 border-b border-[var(--etf-border)] max-md:px-4 max-md:py-5 max-[480px]:py-4 max-[480px]:px-3 max-md:flex-col max-md:items-start max-md:gap-1.5 max-[480px]:gap-1">
        <div className="font-[family-name:var(--font-f37bolton)] text-[24px] max-[480px]:text-[20px] tracking-[-0.02em] text-[var(--etf-white)] flex items-center gap-3 max-[480px]:gap-2 leading-none">
          <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
          Premium / Discount
        </div>
        <div className="font-[family-name:var(--font-geist)] text-[12px] text-[var(--etf-white3)] tracking-[0.08em]">
          Quarterly Data &middot; Since Inception
        </div>
      </div>

      {!hasRealData && (
        <div
          className="relative px-10 pb-7 max-md:px-4"
          aria-label="Premium / discount data unavailable"
        >
          <div
            className="w-full flex items-center justify-center border border-dashed border-[var(--etf-border)] rounded-sm font-[family-name:var(--font-instrument)] text-[12px] leading-[1.7] text-[var(--etf-white3)] tracking-[0.06em] opacity-60"
            style={{ aspectRatio: "800 / 160", minHeight: 120 }}
          >
            Premium / discount history will appear here once available.
          </div>
        </div>
      )}
      {hasRealData && (
        <div className="relative px-10 pb-7 max-md:px-4">
          <svg
            ref={svgRef}
            className="w-full h-auto block"
            viewBox="0 0 800 160"
            preserveAspectRatio="none"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: dataPoints ? "crosshair" : undefined }}
          >
            <defs>
              <linearGradient id="pd-pos" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="rgba(0,200,120,0.25)" />
                <stop offset="100%" stopColor="rgba(0,200,120,0)" />
              </linearGradient>
              <linearGradient id="pd-neg" x1="0" x2="0" y1="1" y2="0">
                <stop offset="0%" stopColor="rgba(230,80,80,0.25)" />
                <stop offset="100%" stopColor="rgba(230,80,80,0)" />
              </linearGradient>
              <filter id="pd-glow">
                <feGaussianBlur result="blur" stdDeviation="2" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <line
              x1="0"
              x2="800"
              y1="80"
              y2="80"
              stroke="rgba(80,74,68,0.4)"
              strokeDasharray="4,4"
              strokeWidth="1"
            />
            <text x="0" y="76" fill="rgba(80,74,68,0.8)" fontFamily="Instrument Sans" fontSize="8">
              0.00%
            </text>
            <text x="0" y="18" fill="rgba(80,74,68,0.6)" fontFamily="Instrument Sans" fontSize="8">
              +{axisMax}%
            </text>
            <text x="0" y="155" fill="rgba(80,74,68,0.6)" fontFamily="Instrument Sans" fontSize="8">
              &minus;{axisMax}%
            </text>
            <path d={fillPath} fill="url(#pd-pos)" opacity="0.5" />
            <polyline
              fill="none"
              filter="url(#pd-glow)"
              points={points}
              stroke="var(--etf-orange)"
              strokeWidth="2"
            />
            {hoverIdx === null && (
              <circle
                cx={endDot.cx}
                cy={endDot.cy}
                r="3"
                fill="var(--etf-orange)"
                filter="url(#pd-glow)"
              />
            )}
            {hoverPoint && (
              <>
                <line
                  x1={hoverPoint.x}
                  x2={hoverPoint.x}
                  y1="0"
                  y2="160"
                  stroke="rgba(255,255,255,0.15)"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
                <circle cx={hoverPoint.x} cy={hoverPoint.y} r="4.5" fill="var(--etf-orange)" />
                <circle
                  cx={hoverPoint.x}
                  cy={hoverPoint.y}
                  r="2.5"
                  fill="var(--etf-black2, #131313)"
                />
              </>
            )}
            {labels.map((label, i) => (
              <text
                key={`${label}-${i}`}
                x={DATE_LABEL_X_POSITIONS[i] ?? 20}
                y="155"
                fill="rgba(80,74,68,0.7)"
                fontFamily="Instrument Sans"
                fontSize="8"
              >
                {label}
              </text>
            ))}
          </svg>

          {hoverPoint && hoverData && (
            <div
              className="absolute pointer-events-none bg-[var(--etf-black2)] border border-[var(--etf-border)] rounded-sm px-3 py-2 min-w-[150px] z-10"
              style={{
                left: `${(hoverPoint.x / 800) * 100}%`,
                top: 0,
                transform: "translateX(-50%)",
              }}
            >
              <div className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.06em] mb-1.5">
                {fmtDate(hoverData.date)}
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0"
                  style={{ background: "var(--etf-orange)" }}
                />
                P/D: {hoverData.pd >= 0 ? "+" : ""}
                {hoverData.pd.toFixed(2)}%
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0 opacity-60"
                  style={{ background: "var(--etf-white3, #9e9488)" }}
                />
                NAV: ${hoverData.nav.toFixed(2)}
              </div>
              <div className="flex items-center gap-1.5 font-[family-name:var(--font-instrument)] text-xs text-[var(--etf-white2)]">
                <span
                  className="w-[6px] h-[6px] rounded-full shrink-0 opacity-60"
                  style={{ background: "var(--etf-white3, #9e9488)" }}
                />
                Mkt: ${hoverData.mkt.toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto mb-7">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] text-left px-10 py-3.5 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Quarter
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] text-right px-10 py-3.5 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at NAV
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] text-right px-10 py-3.5 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at Premium
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] text-right px-10 py-3.5 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                Days at Discount
              </th>
              <th className="font-[family-name:var(--font-instrument)] text-[12px] tracking-[0.14em] uppercase text-[var(--etf-white2)] text-right px-10 py-3.5 border-b border-[var(--etf-border2)] font-normal whitespace-nowrap max-md:px-4 max-md:py-3 max-md:text-[11px]">
                {pdToleranceLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {quarters.map((row, i) => (
              <tr key={`${row.quarter}-${i}`}>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white)] text-left px-10 py-[15px] border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.quarter}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] text-right px-10 py-[15px] border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtNav}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] text-right px-10 py-[15px] border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtPremium}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] text-right px-10 py-[15px] border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.daysAtDiscount}
                </td>
                <td className="font-[family-name:var(--font-instrument)] text-[14px] text-[var(--etf-white2)] text-right px-10 py-[15px] border-b border-[var(--etf-border2)] max-md:px-4 max-md:py-3 max-md:text-[13px]">
                  {row.pdRange}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tracking-[0.04em] leading-[1.7] italic border-t border-[var(--etf-border2)] pt-3 pb-7 px-10">
        The above table and line graph are provided to show the frequency at which the closing price
        of the Fund was at a premium (above) or discount (below) to the Fund&rsquo;s daily net asset
        value (&ldquo;NAV&rdquo;). The table and line graph represent past performance and cannot be
        used to predict future results. Shareholders may pay more than NAV when buying Fund shares
        and receive less than NAV when those shares are sold because shares are bought and sold at
        current{disclaimerEnding}
        {!hasPerformanceHistory && (
          <>
            {" "}
            The Fund is newly organized and has no performance history. Performance data will be
            available after the Fund has completed a sufficient period of operations. Past
            performance does not guarantee future results.
          </>
        )}
      </div>
    </div>
  );
}
